import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { cn } from '@/utils';

interface PopoverProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

const Popover = ({ open, defaultOpen = false, onOpenChange, children }: PopoverProps) => {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
  const currentOpen = open !== undefined ? open : internalOpen;
  
  const handleOpenChange = React.useCallback((newOpen: boolean) => {
    if (onOpenChange) {
      onOpenChange(newOpen);
    } else {
      setInternalOpen(newOpen);
    }
  }, [onOpenChange]);

  return (
    <PopoverContext.Provider value={{ open: currentOpen, onOpenChange: handleOpenChange }}>
      <>{children}</>
    </PopoverContext.Provider>
  );
};
Popover.displayName = 'Popover';

const PopoverContext = React.createContext<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
}>({
  open: false,
  onOpenChange: () => {},
});

interface PopoverTriggerProps {
  asChild?: boolean;
  children: React.ReactNode;
}

const PopoverTrigger = React.forwardRef<HTMLElement, PopoverTriggerProps & React.HTMLAttributes<HTMLElement>>(
  ({ asChild = false, children, ...props }, ref) => {
    const context = React.useContext(PopoverContext);
    
    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(children as React.ReactElement, {
        ref,
        onClick: (e: React.MouseEvent) => {
          context.onOpenChange(!context.open);
          if ((children as React.ReactElement).props.onClick) {
            (children as React.ReactElement).props.onClick(e);
          }
        },
        'aria-expanded': context.open,
        ...props,
      });
    }
    
    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        onClick={() => context.onOpenChange(!context.open)}
        aria-expanded={context.open}
        {...props}
      >
        {children}
      </button>
    );
  }
);
PopoverTrigger.displayName = 'PopoverTrigger';

interface PopoverContentProps {
  className?: string;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  sideOffset?: number;
  children: React.ReactNode;
}

const PopoverContent = React.forwardRef<HTMLDivElement, PopoverContentProps>(
  ({ className, side = 'bottom', align = 'center', sideOffset = 4, children, ...props }, ref) => {
    const context = React.useContext(PopoverContext);
    const triggerRef = React.useRef<HTMLElement | null>(null);
    const [position, setPosition] = React.useState({ top: 0, left: 0 });
    
    if (!context.open) return null;
    
    return ReactDOM.createPortal(
      <div
        ref={ref}
        className={cn(
          'z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          className
        )}
        style={{ position: 'fixed' }}
        {...props}
      >
        {children}
      </div>,
      document.body
    );
  }
);
PopoverContent.displayName = 'PopoverContent';

export { Popover, PopoverTrigger, PopoverContent };